import React, { useCallback, useEffect, useRef } from 'react';
import {
  appendClick,
  createImpression,
  InteractionEventType,
  resetTrackingState,
} from '../../services/interactionTrackingService';
import { logger } from '../../utils/logger';

interface ChatTrackingRootProps {
  sessionId: string | null;
  getToken?: () => Promise<string | undefined>;
  children: React.ReactNode;
}

type CollectedOption = string | Record<string, unknown>;

interface PendingClick {
  eventType: string;
  turnNumber: number;
  interaction: Record<string, unknown>;
  optionsShown: CollectedOption[];
  // Captured at click time so the replay can prefer element-identity over
  // a freshly-synthesized impression. If the element is still mounted
  // when sessionId resolves, we reuse the IO-fired impression (or fire on
  // demand) and the click binds to it; if the element is gone (e.g.
  // welcome panel was unmounted), we fall back to the options snapshot.
  // Without this, replay could create a duplicate impression for a
  // still-mounted element while IO independently creates a second one.
  container: HTMLElement | null;
}

const parseTurn = (el: HTMLElement): number => {
  const raw = el.getAttribute('data-track-turn');
  if (raw === null) return -1;
  const n = parseInt(raw, 10);
  return Number.isNaN(n) ? -1 : n;
};

const readOption = (el: HTMLElement): string => {
  return el.getAttribute('data-track-option') ?? '';
};

const readExtra = (el: HTMLElement): Record<string, unknown> => {
  const raw = el.getAttribute('data-track-extra');
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const FALLBACK_EVENT_TYPE = 'chat_button';
const FALLBACK_PATH_MAX_DEPTH = 5;
const FALLBACK_TEXT_MAX_LEN = 200;
// What counts as "user clicked something" for fallback purposes —
// matches the native clickable surfaces the ARIA model recognizes.
const INTERACTIVE_SELECTOR =
  'button, a, input, select, [role="button"], [role="link"], [role="menuitem"]';

const buildAncestorSelector = (el: HTMLElement): string => {
  // Short tag-only path up to a few ancestors — module-hashed class
  // names carry no meaning cross-deploy, so we skip them.
  const parts: string[] = [];
  let current: HTMLElement | null = el;
  while (current && parts.length < FALLBACK_PATH_MAX_DEPTH) {
    const tag = current.tagName.toLowerCase();
    const id = current.id ? `#${current.id}` : '';
    parts.unshift(`${tag}${id}`);
    current = current.parentElement;
  }
  return parts.join(' > ');
};

const collectOptions = (container: HTMLElement): CollectedOption[] => {
  // Only include options whose nearest [data-track-id] ancestor is
  // `container`. This lets nested containers (e.g. tdg_chip / tdg_season
  // inside a tdg_link card) carry their own option lists without
  // polluting the outer impression.
  //
  // Include the container itself when it doubles as its own option
  // (inline links, tire cards, shop-tires anchor — single elements that
  // carry both data-track-id and data-track-option). querySelectorAll
  // only traverses descendants, so the container needs to be added
  // explicitly.
  //
  // Each option is emitted as `{label, ...extras, url?}` when the element
  // carries `data-track-extra` or is an anchor, so impression payloads
  // record what the user actually saw (URLs, productUrl + position for
  // tire cards, linkType for inline links, etc.) rather than just the
  // label text. Plain options with no extras emit as bare strings to keep
  // simple payloads (starter_prompt, tdg chips) compact.
  const els: HTMLElement[] = container.hasAttribute('data-track-option')
    ? [container]
    : [];
  els.push(
    ...Array.from(
      container.querySelectorAll<HTMLElement>('[data-track-option]'),
    ),
  );
  return els
    .filter((el) => el.closest('[data-track-id]') === container)
    .map((el) => {
      const label = readOption(el);
      if (!label) return null;
      const extras = readExtra(el);
      const href =
        el instanceof HTMLAnchorElement && el.href ? el.href : undefined;
      const hasUrl = href || 'url' in extras || 'productUrl' in extras;
      const hasExtras = Object.keys(extras).length > 0;
      if (!hasExtras && !hasUrl) return label;
      const obj: Record<string, unknown> = { label, ...extras };
      if (href && !('url' in extras) && !('productUrl' in extras)) {
        obj.url = href;
      }
      return obj;
    })
    .filter((o): o is CollectedOption => o !== null);
};

/**
 * Delegated click + impression tracking for the chat surface (TRAC-527).
 *
 * Children annotate containers with `data-track-id` (= eventType) and
 * option elements with `data-track-option` (= `interaction.selected`).
 * Optional `data-track-extra` on an option element carries extra keys
 * spread into the click `interaction`. Containers also carry
 * `data-track-turn` so click/impression payloads know the turn without
 * threading React state through the DOM.
 *
 * Impressions fire via IntersectionObserver on first visibility; clicks
 * fire via a single delegated listener on this component's root.
 * Clicks fired before sessionId resolves are queued with an options
 * snapshot and replayed once sessionId is available.
 */
const ChatTrackingRoot: React.FC<ChatTrackingRootProps> = ({
  sessionId,
  getToken,
  children,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);

  // Element-identity cache. One impression per DOM element instance:
  //   - Re-renders of the same element find the same sortKey and don't
  //     re-fire.
  //   - React unmount + remount = new element = new impression. Lets the
  //     same (eventType, turnNumber) recur naturally — re-opening the
  //     YMMV typeahead, drilling back through year/make, etc.
  //   - Detached elements get GC'd along with their cache entry, so
  //     long-lived sessions don't leak.
  // String-keyed dedup (sessionId:eventType:turnNumber) didn't work for
  // those cases — it collapsed every recurrence into one sortKey, leaving
  // later clicks orphaned to a stale impression.
  const sortKeysByElementRef = useRef<WeakMap<HTMLElement, string>>(
    new WeakMap(),
  );
  const pendingClicksRef = useRef<PendingClick[]>([]);

  const canTrack = !!sessionId && !!getToken;

  // Reset on sessionId *rotation* — not on initial set. Long-lived
  // elements (vehicle header, chat shell) survive a rotation, and without
  // a reset their cached sortKey would belong to the old session, so a
  // click on one of them would emit a click trace tagged with the new
  // sessionId but referencing an impression from the old session.
  //
  // Initial mount (null → value) doesn't need a reset — caches are
  // already empty. Resetting unconditionally on every truthy value caused
  // duplicate impressions in two cases: (a) React StrictMode dev double-
  // mount, and (b) any churn during pre-conversation that landed sessionId
  // through a transient non-null value before settling. In both cases IO
  // re-fired on the same still-visible element after the WeakMap was
  // wiped.
  const prevSessionIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!sessionId) return;
    const prev = prevSessionIdRef.current;
    if (prev !== null && prev !== sessionId) {
      resetTrackingState();
      sortKeysByElementRef.current = new WeakMap();
    }
    prevSessionIdRef.current = sessionId;
  }, [sessionId]);

  const fireImpression = useCallback(
    (container: HTMLElement): string | null => {
      if (!sessionId || !getToken) return null;
      const eventType = container.getAttribute('data-track-id');
      if (!eventType) return null;

      const cached = sortKeysByElementRef.current.get(container);
      if (cached) return cached;

      const optionsShown = collectOptions(container);
      // Mirrors the pre-refactor guard in MessageRenderer: skip empty
      // option lists (prevents logging a welcome-flash impression before
      // starter prompts resolve). The element stays uncached so a later
      // re-render with populated options will fire the real impression.
      if (optionsShown.length === 0) {
        return null;
      }

      const turnNumber = parseTurn(container);
      const sortKey = createImpression(
        {
          sessionId,
          turnNumber,
          eventType: eventType as InteractionEventType,
          optionsShown,
        },
        getToken,
      );
      sortKeysByElementRef.current.set(container, sortKey);
      return sortKey;
    },
    [sessionId, getToken],
  );

  // Observe [data-track-id] containers for first visibility; also catch
  // nodes added after the observer mounts (new turns, streaming parts).
  useEffect(() => {
    const node = rootRef.current;
    if (!node) return;

    const intersection = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const fired = fireImpression(entry.target as HTMLElement);
          // Only unobserve when we actually got a sortKey. If the
          // container rendered empty (no options yet, e.g. async fetch
          // hasn't resolved), keep it observed — the MO retry below will
          // re-fire fireImpression once children populate. IO itself
          // doesn't re-fire on a continuously-visible element, so without
          // the MO retry we'd permanently miss the impression.
          if (fired) intersection.unobserve(entry.target);
        }
      },
      { root: null, threshold: 0 },
    );

    const observeAllIn = (root: HTMLElement) => {
      const observeIfEligible = (el: Element) => {
        if (!(el instanceof HTMLElement)) return;
        // Opt-out: containers that want click-time impression creation
        // (e.g. inline markdown links, matching pre-refactor behavior).
        if (el.hasAttribute('data-track-defer-impression')) return;
        intersection.observe(el);
      };
      if (root.hasAttribute?.('data-track-id')) observeIfEligible(root);
      root.querySelectorAll?.('[data-track-id]').forEach(observeIfEligible);
    };

    observeAllIn(node);

    const mutation = new MutationObserver((mutations) => {
      // Collect ancestors of any added node that look like a tracking
      // container — once a child appears, the container's options may now
      // be populated. fireImpression is idempotent via the WeakMap cache,
      // so calling it on an already-fired container is a no-op. Set
      // dedups so we don't fireImpression N times in one batch.
      const containersToRetry = new Set<HTMLElement>();
      for (const m of mutations) {
        m.addedNodes.forEach((added) => {
          if (!(added instanceof HTMLElement)) return;
          observeAllIn(added);
          const ancestor = added.parentElement?.closest(
            '[data-track-id]',
          ) as HTMLElement | null;
          if (ancestor && !sortKeysByElementRef.current.has(ancestor)) {
            containersToRetry.add(ancestor);
          }
        });
      }
      containersToRetry.forEach((container) => {
        const fired = fireImpression(container);
        if (fired) intersection.unobserve(container);
      });
    });
    mutation.observe(node, { childList: true, subtree: true });

    return () => {
      intersection.disconnect();
      mutation.disconnect();
    };
  }, [fireImpression]);

  // When sessionId resolves, replay any queued clicks. Prefer element
  // identity when the original container is still in the DOM — the IO
  // observer will (or did) fire its impression for the same element, and
  // we want this click to bind to that same sortKey rather than create a
  // duplicate. If the container has been unmounted (welcome message →
  // starter prompt send), fall back to synthesizing a fresh impression
  // from the snapshot. Ordering at Langfuse is handled server-side via
  // the sortKey's ISO timestamp.
  useEffect(() => {
    if (!canTrack || pendingClicksRef.current.length === 0) return;
    const pending = pendingClicksRef.current;
    pendingClicksRef.current = [];
    for (const p of pending) {
      let sortKey: string | undefined;
      if (p.container && p.container.isConnected) {
        // Element-identity path: reuses an IO-fired sortKey via the
        // WeakMap, or fires on demand. Either way the click binds to
        // the same impression a real-time click would.
        sortKey =
          sortKeysByElementRef.current.get(p.container) ??
          fireImpression(p.container) ??
          undefined;
      }
      if (!sortKey && p.optionsShown.length > 0) {
        // Container gone — synthesize an impression from the snapshot.
        sortKey = createImpression(
          {
            sessionId: sessionId!,
            turnNumber: p.turnNumber,
            eventType: p.eventType as InteractionEventType,
            optionsShown: p.optionsShown,
          },
          getToken!,
        );
      }
      if (!sortKey) continue;
      appendClick(
        {
          sessionId: sessionId!,
          sortKey,
          interaction: p.interaction,
          eventType: p.eventType,
          turnNumber: p.turnNumber,
        },
        getToken!,
      );
    }
  }, [canTrack, sessionId, getToken]);

  useEffect(() => {
    const node = rootRef.current;
    if (!node) return;

    // Plain synchronous listener: dispatch impression + click POSTs as
    // fire-and-forget (keepalive ensures they survive page unload). No
    // client-side serialization — the backend pins each trace's Langfuse
    // timestamp to the sortKey's ISO time, so ordering is preserved even
    // when POSTs race through the network.
    const handler = (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;

      const container = target.closest('[data-track-id]') as HTMLElement | null;

      if (container) {
        const eventType = container.getAttribute('data-track-id');
        if (!eventType) return;

        // Only fire on clicks that actually land on a track-option — keeps
        // us from logging empty-`selected` clicks when users hit inert
        // areas inside a tracked container (e.g. TDG card whitespace).
        const optionEl = target.closest(
          '[data-track-option]',
        ) as HTMLElement | null;
        if (!optionEl) return;

        const turnNumber = parseTurn(container);
        const selected = readOption(optionEl);
        const extra = readExtra(optionEl);

        const interaction = {
          selected,
          timestamp: new Date().toISOString(),
          ...extra,
        };

        if (!sessionId || !getToken) {
          pendingClicksRef.current.push({
            eventType,
            turnNumber,
            interaction,
            optionsShown: collectOptions(container),
            container,
          });
          return;
        }

        // Element-identity lookup: if IO already fired for this container
        // we reuse its sortKey; otherwise fire on demand.
        let sortKey = sortKeysByElementRef.current.get(container);
        if (!sortKey) {
          sortKey = fireImpression(container) ?? undefined;
        }
        if (!sortKey) {
          // The container had no options when we tried to fire — most
          // likely the click landed during a streaming render where the
          // option list isn't yet collectable. Surface it so we have a
          // signal if this turns out to be common in production.
          logger.warn(
            '[tracking] click dropped: no impression for container',
            { eventType, turnNumber, selected },
          );
          return;
        }

        appendClick(
          {
            sessionId,
            sortKey,
            interaction,
            eventType,
            turnNumber,
          },
          getToken,
        );
        return;
      }

      // Fallback: the click landed inside the tracking root but didn't
      // hit any annotated container. Log as `chat_button` so new
      // interactive UI is never silently untracked — the floor is
      // "everything gets logged", granular typing is opt-in upgrade.
      const interactive = target.closest(
        INTERACTIVE_SELECTOR,
      ) as HTMLElement | null;
      if (!interactive) return;

      const rawText = (interactive.textContent ?? '')
        .trim()
        .replace(/\s+/g, ' ')
        .slice(0, FALLBACK_TEXT_MAX_LEN);
      const ariaLabel = interactive.getAttribute('aria-label') ?? undefined;
      // Icon-only controls (feedback thumbs, close buttons) carry their
      // meaning in aria-label rather than textContent — fall back to it
      // so those clicks still land instead of silently dropping.
      const text = rawText || ariaLabel?.trim().slice(0, FALLBACK_TEXT_MAX_LEN) || '';
      if (!text) return;

      const href =
        interactive instanceof HTMLAnchorElement ? interactive.href : undefined;
      const messageEl = interactive.closest(
        '[data-message-id]',
      ) as HTMLElement | null;
      const messageId = messageEl?.getAttribute('data-message-id') ?? undefined;
      const messageRole =
        messageEl?.getAttribute('data-message-role') ?? undefined;
      const turnAttr = messageEl?.getAttribute('data-message-turn');
      const turnNumber =
        turnAttr !== null && turnAttr !== undefined ? parseInt(turnAttr, 10) : -1;

      const interaction: Record<string, unknown> = {
        selected: text,
        timestamp: new Date().toISOString(),
        ancestorSelector: buildAncestorSelector(interactive),
      };
      if (href) interaction.url = href;
      if (ariaLabel && ariaLabel !== text) interaction.ariaLabel = ariaLabel;
      if (messageId) interaction.messageId = messageId;
      if (messageRole) interaction.messageRole = messageRole;

      if (!sessionId || !getToken) {
        pendingClicksRef.current.push({
          eventType: FALLBACK_EVENT_TYPE,
          turnNumber,
          interaction,
          // One stand-in option so the impression record is well-formed;
          // individual click text lives in `interaction.selected`.
          optionsShown: [FALLBACK_EVENT_TYPE],
          // Fallback path has no annotated container; replay always
          // synthesizes a fresh impression.
          container: null,
        });
        return;
      }

      // Cache the fallback impression per message element so all fallback
      // clicks within the same message correlate via one sortKey.
      // No message context (e.g. site-chrome buttons): cache per
      // interactive element instead.
      const cacheEl = messageEl ?? interactive;
      let sortKey = sortKeysByElementRef.current.get(cacheEl);
      if (!sortKey) {
        sortKey = createImpression(
          {
            sessionId,
            turnNumber,
            eventType: FALLBACK_EVENT_TYPE as InteractionEventType,
            optionsShown: [FALLBACK_EVENT_TYPE],
          },
          getToken,
        );
        sortKeysByElementRef.current.set(cacheEl, sortKey);
      }

      appendClick(
        {
          sessionId,
          sortKey,
          interaction,
          eventType: FALLBACK_EVENT_TYPE,
          turnNumber,
        },
        getToken,
      );
    };

    node.addEventListener('click', handler);
    return () => node.removeEventListener('click', handler);
  }, [sessionId, getToken, fireImpression]);

  return (
    <div ref={rootRef} style={{ display: 'contents' }}>
      {children}
    </div>
  );
};

export default ChatTrackingRoot;
